'use client';
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://meli.tevecompras.com';

const AuthContext = createContext({
  user: null,
  login: () => {},
  logout: () => {},
  isAdmin: false,
  permissions: {},
  permissionsLoaded: false,
  reloadPermissions: () => {},
  hasPermission: () => false,
});
export const useAuth = () => useContext(AuthContext);

// ── Decodifica JWT sin verificar firma (solo para leer exp) ──
function decodeJWT(token) {
  try {
    return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch { return null; }
}

function isTokenExpiringSoon(token) {
  const decoded = decodeJWT(token);
  if (!decoded?.exp) return true;
  const secsLeft = decoded.exp - Math.floor(Date.now() / 1000);
  return secsLeft < 300;
}

async function doRefresh(refreshToken) {
  try {
    const res = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.accessToken || null;
  } catch { return null; }
}

export async function authFetch(path, options = {}) {
  let tokens = JSON.parse(localStorage.getItem('mp-tokens') || '{}');
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  if (tokens.accessToken && tokens.refreshToken && isTokenExpiringSoon(tokens.accessToken)) {
    const newToken = await doRefresh(tokens.refreshToken);
    if (newToken) {
      tokens.accessToken = newToken;
      localStorage.setItem('mp-tokens', JSON.stringify(tokens));
    }
  }

  const res = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      ...(tokens.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {}),
    },
  });

  if (res.status === 401 && tokens.refreshToken) {
    const newToken = await doRefresh(tokens.refreshToken);
    if (newToken) {
      tokens.accessToken = newToken;
      localStorage.setItem('mp-tokens', JSON.stringify(tokens));
      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${newToken}`,
        },
      });
    }
    localStorage.removeItem('mp-tokens');
    localStorage.removeItem('mp-user');
    window.dispatchEvent(new Event('session-expired'));
  }

  return res;
}

const ADMIN_ROLES = ['admin', 'superadmin', 'Admin', 'Administrador'];

// Jerarquía de permisos (debe espejear services/permissions.js del backend)
const PERMISSION_LEVELS = { read: 1, write: 2, admin: 3 };

export function AuthProvider({ children, loginView, appView }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({});     // { flex: ['read','write'], seller_campaigns: ['read'], ... }
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const refreshIntervalRef = useRef(null);

  const scheduleTokenCheck = useCallback(() => {
    if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);

    refreshIntervalRef.current = setInterval(async () => {
      const tokens = JSON.parse(localStorage.getItem('mp-tokens') || '{}');
      if (!tokens.accessToken || !tokens.refreshToken) return;

      if (isTokenExpiringSoon(tokens.accessToken)) {
        console.log('[AUTH] Token expirando pronto, renovando...');
        const newToken = await doRefresh(tokens.refreshToken);
        if (newToken) {
          tokens.accessToken = newToken;
          localStorage.setItem('mp-tokens', JSON.stringify(tokens));
          console.log('[AUTH] Token renovado proactivamente');
        } else {
          console.warn('[AUTH] Refresh falló, cerrando sesión');
          clearInterval(refreshIntervalRef.current);
          localStorage.removeItem('mp-tokens');
          localStorage.removeItem('mp-user');
          setUser(null);
          window.dispatchEvent(new Event('session-expired'));
        }
      }
    }, 4 * 60 * 1000);
  }, []);

  // ── Carga de permisos del backend ──
  const reloadPermissions = useCallback(async () => {
    try {
      const res = await authFetch('/api/permissions/me');
      if (!res.ok) {
        setPermissions({});
        setPermissionsLoaded(true);
        return;
      }
      const data = await res.json();
      setPermissions(data.permissions || {});
      setPermissionsLoaded(true);
    } catch (err) {
      console.warn('[AUTH] No se pudieron cargar permisos:', err.message);
      setPermissions({});
      setPermissionsLoaded(true);
    }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('mp-user');
    if (saved) {
      try {
        const u = JSON.parse(saved);
        setUser(u);
        scheduleTokenCheck();
        reloadPermissions();
      } catch {}
    }
    setLoading(false);

    const handler = () => {
      setUser(null);
      setPermissions({});
      setPermissionsLoaded(false);
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
    window.addEventListener('session-expired', handler);
    return () => {
      window.removeEventListener('session-expired', handler);
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, [scheduleTokenCheck, reloadPermissions]);

  const login = useCallback(async (username, password) => {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Error de autenticación');
    }

    const data = await res.json();

    const u = {
      username: data.user.username,
      displayName: data.user.displayName,
      email: data.user.email,
      department: data.user.department,
      roles: data.user.roles || ['viewer'],
      name: data.user.displayName || data.user.username,
    };

    setUser(u);
    localStorage.setItem('mp-user', JSON.stringify(u));
    localStorage.setItem('mp-tokens', JSON.stringify({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
    }));

    scheduleTokenCheck();
    await reloadPermissions();   // cargar permisos inmediatamente post-login
    return u;
  }, [scheduleTokenCheck, reloadPermissions]);

  const logout = useCallback(async () => {
    const tokens = JSON.parse(localStorage.getItem('mp-tokens') || '{}');
    if (tokens.refreshToken) {
      try {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: tokens.refreshToken }),
        });
      } catch (_) {}
    }
    if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    setUser(null);
    setPermissions({});
    setPermissionsLoaded(false);
    localStorage.removeItem('mp-user');
    localStorage.removeItem('mp-tokens');
  }, []);

  const isAdmin = user?.roles?.some((r) => ADMIN_ROLES.includes(r)) || false;

  // ── hasPermission(module, permission): chequea jerarquía localmente ──
  // Si es admin LDAP, devuelve true siempre (bypass).
  const hasPermission = useCallback((module, permission) => {
    if (isAdmin) return true;
    if (!module || !permission) return false;
    const userPerms = permissions[module] || [];
    if (userPerms.length === 0) return false;
    const requiredLevel = PERMISSION_LEVELS[permission] || 0;
    const maxUserLevel  = Math.max(...userPerms.map((p) => PERMISSION_LEVELS[p] || 0));
    return maxUserLevel >= requiredLevel;
  }, [isAdmin, permissions]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-0)' }}>
        <div className="text-sm" style={{ color: 'var(--text-2)' }}>Cargando...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{
      user, login, logout, isAdmin,
      permissions, permissionsLoaded, reloadPermissions, hasPermission,
    }}>
      {user ? appView : loginView}
    </AuthContext.Provider>
  );
}

// ════════════════════════════════════════════════════════════
// Hook conveniencia: usePermission('flex', 'write')
// ════════════════════════════════════════════════════════════
export function usePermission(module, permission) {
  const { hasPermission, permissionsLoaded } = useAuth();
  return {
    granted: hasPermission(module, permission),
    loaded: permissionsLoaded,
  };
}